import { useState } from 'react';
import Button from 'src/components/button/button';
import { useImportConfiguration } from 'src/context/import-configuration';
import {
  globalSettingsTabs,
  GlobalSettingsNtpSection,
  GlobalSettingsUnusedPortsSection,
  GlobalSettingsUserCredentialsSection,
} from 'src/modules/network-configuration/dialog/global-settings/global-settings-extra-sections';
import {
  GlobalSettingsIgnitionSection,
  GlobalSettingsRemoteLoggingSection,
} from 'src/modules/network-configuration/dialog/global-settings/global-settings-sections';
import { twMerge } from 'tailwind-merge';

export default function ImportStepGlobalSettings() {
  const [currentTab, setCurrentTab] = useState<string>(
    globalSettingsTabs[0].key,
  );
  const {
    dialogState,
    updateProjectJsonData,
    importReview,
    setGlobalSettingsConfirmed,
  } = useImportConfiguration();

  const invalidate = () => setGlobalSettingsConfirmed(false);

  if (!dialogState?.projectJsonData) return null;

  const gs = dialogState.projectJsonData.globalSettings || {};
  const onChangeGlobalSettings = (next: typeof gs) => {
    updateProjectJsonData((prev) => ({
      ...prev,
      globalSettings: {
        ...prev.globalSettings,
        ...next,
      },
    }));
    invalidate();
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-gray-600">
          Review global settings from the imported configuration. Confirm before
          the summary step.
        </p>
        <Button
          type="button"
          color={importReview.globalSettingsConfirmed ? 'green' : 'blue'}
          onClick={() => setGlobalSettingsConfirmed(true)}
        >
          {importReview.globalSettingsConfirmed
            ? 'Confirmed ✓'
            : 'Confirm step'}
        </Button>
      </div>

      <div className="rounded border border-gray-200 bg-white p-3">
        <div className="relative border-b border-blue-400 pb-0.5">
          <nav
            aria-label="Tabs"
            className="-mb-px flex space-x-4 overflow-x-auto"
          >
            {globalSettingsTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                aria-current={currentTab === tab.key ? 'page' : undefined}
                className={twMerge([
                  'text-md whitespace-nowrap border-b-2 px-2 py-1 font-medium',
                  currentTab === tab.key
                    ? 'border-blue-400 bg-blue-400 text-white'
                    : 'border-transparent text-gray-500',
                ])}
                onClick={() => setCurrentTab(tab.key)}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        <div className="mt-4">
          {currentTab === 'unused-ports' && (
            <GlobalSettingsUnusedPortsSection
              values={gs}
              onChange={onChangeGlobalSettings}
            />
          )}
          {currentTab === 'ignition' && (
            <GlobalSettingsIgnitionSection
              values={gs}
              onChange={onChangeGlobalSettings}
            />
          )}
          {currentTab === 'remote-logging' && (
            <GlobalSettingsRemoteLoggingSection
              values={gs}
              onChange={onChangeGlobalSettings}
            />
          )}
          {currentTab === 'ntp' && (
            <GlobalSettingsNtpSection
              values={gs}
              onChange={onChangeGlobalSettings}
            />
          )}
          {currentTab === 'user-credentials' && (
            <GlobalSettingsUserCredentialsSection
              values={gs}
              onChange={onChangeGlobalSettings}
            />
          )}
        </div>
      </div>
    </div>
  );
}
